import type { UsageView } from "@dentos/contracts";
import { useQuery } from "@tanstack/react-query";
import { Badge, Card, ErrorState, Loading, cx } from "../components/ui";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import { useClinicFormat } from "../lib/hooks";
import { useSession } from "../lib/session";
import { useBranding } from "../lib/branding";

/** Days until an ISO time (rounded up), for trial messages. */
export const daysLeft = (iso: string, now = Date.now()) => Math.max(0, Math.ceil((Date.parse(iso) - now) / 86_400_000));

/** Settings → Plan & usage: the clinic's plan, trial end, limits with current use, and included features. */
export function PlanUsageCard() {
  const { locale, timeZone } = useClinicFormat();
  const support = useSupportLine();
  const q = useQuery({ queryKey: ["usage"], queryFn: () => api.get<UsageView>("/api/usage") });
  return (
    <Card title="Plan & usage">
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div className="space-y-4 text-sm">
          {q.data.plan ? (
            <p className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{q.data.plan.name}</span>
              {q.data.plan.status === "trialing" ? <Badge tone="amber">Trial</Badge> : <Badge tone="green">Active</Badge>}
              {q.data.plan.trialEndsAt && (
                <span className="text-slate-600">
                  ends {formatDate(q.data.plan.trialEndsAt, locale, timeZone)} ({daysLeft(q.data.plan.trialEndsAt)} days left)
                </span>
              )}
              {q.data.plan.periodEnd && <span className="text-slate-600">paid until {formatDate(q.data.plan.periodEnd, locale, timeZone)}</span>}
            </p>
          ) : (
            <p className="text-slate-600">No active plan.</p>
          )}
          <ul className="space-y-2">
            {q.data.limits.map((l) => {
              const pct = l.limit ? Math.min(100, Math.round((l.used / l.limit) * 100)) : 0;
              return (
                <li key={l.featureKey}>
                  <div className="flex justify-between">
                    <span>{l.label}</span>
                    <span className="tabular-nums text-slate-600">
                      {l.used.toLocaleString(locale)} {l.limit === null ? "(no limit)" : `of ${l.limit.toLocaleString(locale)}`}
                    </span>
                  </div>
                  {l.limit !== null && (
                    <div className="mt-1 h-1.5 rounded bg-slate-100" aria-hidden>
                      <div className={cx("h-1.5 rounded", pct >= 100 ? "bg-red-600" : pct >= 80 ? "bg-amber-500" : "bg-brand-600")} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap gap-1">
            {q.data.features.map((f) => (
              <Badge key={f.featureKey} tone={f.enabled ? "brand" : "gray"}>
                {f.enabled ? "" : "Not included: "}
                {f.label}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-slate-500">To change your plan, contact {support}.</p>
        </div>
      )}
    </Card>
  );
}

/** Thin banner for people who manage the clinic while it is on a trial. */
export function TrialBanner() {
  const { session, can } = useSession();
  const support = useSupportLine();
  const end = session?.activeTenant?.trialEndsAt;
  if (!end || !can("settings.manage")) return null;
  const d = daysLeft(end);
  return (
    <div className={cx("mb-4 rounded-lg px-3 py-2 text-sm", d <= 3 ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200" : "bg-brand-50/50 text-brand-900")}>
      Free trial: {d === 0 ? "ends today" : `${d} day${d === 1 ? "" : "s"} left`}. Contact {support} to choose a plan and keep your clinic running.
    </div>
  );
}

/** "VaidyaOS support (+91 …, help@…)": the product's support contacts from Platform admin → Branding. */
function useSupportLine() {
  const b = useBranding();
  const contacts = [b.supportPhone, b.supportEmail].filter(Boolean).join(", ");
  return `${b.productName} support${contacts ? ` (${contacts})` : ""}`;
}
